import React from 'react';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { Box, IconButton, TableCell, TextField } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

type SortDirection = 'asc' | 'desc' | null;

interface TableFilterCellProps {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  width?: number | string;
  sortDirection?: SortDirection;
  onSort?: () => void;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  cellSx?: SxProps<Theme>;
  textFieldSx?: SxProps<Theme>;
}

const resolveWidth = (width?: number | string) => {
  if (width == null) {
    return undefined;
  }

  return typeof width === 'number' ? `${width}px` : width;
};

export const TableFilterCell: React.FC<TableFilterCellProps> = ({
  placeholder,
  value,
  onChange,
  width,
  sortDirection = null,
  onSort,
  align = 'left',
  cellSx,
  textFieldSx,
}) => {
  const resolvedWidth = resolveWidth(width);
  const hasSort = Boolean(onSort);

  return (
    <TableCell
      align={align}
      sx={{
        py: 0.75,
        px: 1,
        whiteSpace: 'nowrap',
        verticalAlign: 'bottom',
        ...(resolvedWidth ? { width: resolvedWidth, maxWidth: resolvedWidth } : {}),
        ...cellSx,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <TextField
          size="small"
          variant="standard"
          fullWidth
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onClick={(event) => event.stopPropagation()}
          inputProps={{
            style: {
              textAlign: 'left',
            },
          }}
          sx={{
            minWidth: 0,
            '& .MuiInputBase-root': {
              fontSize: '0.875rem',
              justifyContent: 'flex-start',
            },
            '& .MuiInputBase-input': {
              py: 0.75,
              pr: hasSort ? 3 : 0,
            },
            '& .MuiInputBase-input::placeholder': {
              opacity: 1,
            },
            ...textFieldSx,
          }}
        />
        {hasSort && onSort && (
          <IconButton
            size="small"
            onClick={onSort}
            sx={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              p: 0.25,
              width: '20px',
              height: '20px',
              flexShrink: 0,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            {sortDirection === 'asc' ? (
              <ArrowUpward sx={{ fontSize: '0.75rem', color: 'primary.main' }} />
            ) : sortDirection === 'desc' ? (
              <ArrowDownward sx={{ fontSize: '0.75rem', color: 'primary.main' }} />
            ) : (
              <ArrowUpward sx={{ fontSize: '0.625rem', color: 'action.disabled', opacity: 0.5 }} />
            )}
          </IconButton>
        )}
      </Box>
    </TableCell>
  );
};
